import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MenuGridSkeleton } from "./SkeletonLoader";
import HeartIcon from "./HeartIcon";
import TiltCard from "./TiltCard";

import { API, authFetch } from "../api";
import ItemDetailSheet from "./ItemDetailSheet";
import { useToast } from "./Toast";
const CATS = [
  { key: "all", label: "All" },
  { key: "shawarma", label: "Shawarma" },
  { key: "pizza", label: "Pizza" },
  { key: "burger", label: "Burger" },
  { key: "sandwich", label: "Sandwich" },
  { key: "hotdog", label: "Hot Dog" },
  { key: "sub", label: "Sub" },
  { key: "pasta", label: "Pasta" },
  { key: "toast", label: "Toast" },
  { key: "starters", label: "Starters" },
  { key: "salad", label: "Salad" },
  { key: "mojito", label: "Mojito" },
  { key: "milkshakes", label: "Milk Shakes" },
  { key: "freshjuice", label: "Fresh Juice" },
  { key: "desserts", label: "Desserts" },
];


export default function MenuScreen({
  user, cart, cartCount, cartTotal,
  addItem, removeItem,
  onCartClick, onAdminClick, onLogout, onProfileClick
}) {
  const [cat, setCat] = useState("all");
  const [search, setSearch] = useState("");
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailItem, setDetailItem] = useState(null);
  const [vegFilter, setVegFilter] = useState("all");
  const [favorites, setFavorites] = useState(new Set());
  const [poppingId, setPoppingId] = useState(null);
  const [cartPulse, setCartPulse] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [shakingId, setShakingId] = useState(null);
  const [searchHistory, setSearchHistory] = useState(() => { try { return JSON.parse(localStorage.getItem("fb_search_history") || "[]"); } catch { return []; } });
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [comboSuggestion, setComboSuggestion] = useState(null); // { item, suggestion }
  const { showToast } = useToast();

  // Category pairings for combo suggestions
  const COMBOS = {
    burger: ["mojito", "milkshakes", "freshjuice"],
    shawarma: ["mojito", "freshjuice"],
    pizza: ["mojito", "freshjuice"],
    hotdog: ["freshjuice", "mojito"],
    sub: ["freshjuice", "mojito"],
    sandwich: ["freshjuice", "milkshakes"],
    pasta: ["freshjuice", "mojito"],
    starters: ["mojito", "freshjuice"],
  };

  const getSuggestion = (item) => {
    const targets = COMBOS[item.category] || [];
    for (const target of targets) {
      const found = menu.find(m => m.category === target && m.available && m.id !== item.id && !cart[m.id]);
      if (found) return found;
    }
    return null;
  };

  const suggestions = search.trim().length > 0
    ? menu.filter(i => i.name.toLowerCase().includes(search.toLowerCase())).slice(0, 5)
    : [];

  useEffect(() => {
    // Try to get menu from localStorage first (cached)
    const cachedRaw = localStorage.getItem("fb_menu_cache");
    const cachedAt = parseInt(localStorage.getItem("fb_menu_cache_ts") || "0");
    const fresh = Date.now() - cachedAt < 5 * 60 * 1000; // 5 min TTL
    const cachedMenu = fresh ? cachedRaw : null;
    const fetchMenuWithRetry = async (retries = 3, delay = 1000) => {
      try {
        const r = await fetch(`${API}/menu`);
        if (!r.ok) throw new Error("Server returned " + r.status);
        const data = await r.json();
        localStorage.setItem("fb_menu_cache", JSON.stringify(data));
        localStorage.setItem("fb_menu_cache_ts", Date.now().toString());
        setMenu(data);
        setLoading(false);
      } catch (e) {
        if (retries > 0) {
          setTimeout(() => fetchMenuWithRetry(retries - 1, delay * 2), delay);
        } else {
          showToast("Failed to load menu. Auto-retrying...", "error");
          // Keep retrying in the background with exponential backoff capped at 30 seconds
          const nextDelay = Math.min(delay * 2, 30000);
          const jitterDelay = nextDelay + Math.random() * 1000;
          setTimeout(() => fetchMenuWithRetry(0, nextDelay), jitterDelay);
        }
      }
    };

    if (cachedMenu) {
      setMenu(JSON.parse(cachedMenu));
      setLoading(false);
      // Still fetch fresh data in background silently
      fetch(`${API}/menu`).then(r => r.json()).then(data => {
        localStorage.setItem("fb_menu_cache", JSON.stringify(data));
        localStorage.setItem("fb_menu_cache_ts", Date.now().toString());
        setMenu(data);
      }).catch(e => console.error("Background refresh failed:", e));
    } else {
      // No cache, fetch from API with retry
      fetchMenuWithRetry();
    }

    // Fetch favorites
    authFetch(`${API}/favorites`)
      .then(r => r.json())
      .then(data => setFavorites(new Set(data.map(f => f.id))))
      .catch((e) => console.error("Favorites fetch failed:", e));
    // Fetch announcements
    fetch(`${API}/announcements`).then(r => r.json()).then(setAnnouncements).catch((e) => console.error("Announcements fetch failed:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  const filtered = menu.filter(i => {
    // Category filter
    const catMatch = cat === "all" || i.category === cat;

    // Search filter
    const searchMatch = !search || i.name.toLowerCase().includes(search.toLowerCase());

    // Veg/Non-veg filter - handle both "veg" and "nonveg" values
    const itemType = i.type || "veg"; // Default to veg if not set
    let vegMatch = true;
    if (vegFilter === "veg") vegMatch = itemType === "veg";
    else if (vegFilter === "non-veg") vegMatch = itemType === "nonveg"; // Match with no hyphen

    return catMatch && searchMatch && vegMatch;
  });

  // Calculate count for each category
  const getCategoryCount = (catKey) => {
    if (catKey === "all") return menu.length;
    return menu.filter(i => i.category === catKey).length;
  };

  const initials = (user?.name || "U").trim().split(/\s+/).map(w => w[0] || "").join("").toUpperCase().slice(0, 2) || "U";

  const handleCardClick = (item) => { setDetailItem(item); };

  const handleAddClick = (item) => {
    if ((cart[item.id]?.qty || 0) >= 10) {
      showToast("Max limit reached (10 items)", "error");
      setTimeout(() => setShakingId(null), 400);
      return;
    }
    if (item.addons && item.addons.length > 0) {
      setDetailItem(item);
    } else {
      addItem(item, []);
      setPoppingId(item.id);
      setCartPulse(true);
      if (navigator.vibrate) navigator.vibrate(40);
      setTimeout(() => setPoppingId(null), 350);
      setTimeout(() => setCartPulse(false), 550);
      // Show combo suggestion
      const suggestion = getSuggestion(item);
      if (suggestion) {
        setComboSuggestion({ added: item, suggestion });
        setTimeout(() => setComboSuggestion(null), 4000);
      }
    }
  };

  const handleAddonConfirm = (item, selectedAddons) => {
    addItem(item, selectedAddons);
    setDetailItem(null);
  };

  const toggleFavorite = async (itemId) => {
    try {
      const response = await authFetch(`${API}/favorites/${itemId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });

      if (response.ok) {
        setFavorites(prev => {
          const updated = new Set(prev);
          if (updated.has(itemId)) {
            updated.delete(itemId);
          } else {
            updated.add(itemId);
            if (navigator.vibrate) navigator.vibrate(60);
            showToast("Added to favorites", "favorite");
          }
          return updated;
        });
      }
    } catch (error) {
    }
  };

  return (
    <div
      style={{ background: "#FAFAF8", minHeight: "100vh", paddingBottom: "calc(130px + env(safe-area-inset-bottom))", position: "relative" }}
    >
      <div className="screen-glow" />
      {/* Announcement marquee ticker */}
      {announcements.filter(a => !dismissedIds.has(a.id)).length > 0 && (
        <div className="announcement-ticker-container">
          <div className="announcement-ticker-icon">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
          </div>
          <div className="announcement-ticker-track">
            <div className="announcement-ticker-text">
              {announcements.filter(a => !dismissedIds.has(a.id)).map((a, i, arr) => (
                <span key={a.id} className="announcement-ticker-item">
                  {a.message}
                  {i < arr.length - 1 && <span className="ticker-divider">•</span>}
                </span>
              ))}
            </div>
          </div>
          <button 
            onClick={() => {
              const activeIds = announcements.filter(a => !dismissedIds.has(a.id)).map(a => a.id);
              setDismissedIds(prev => new Set([...prev, ...activeIds]));
            }}
            className="announcement-ticker-close"
            title="Dismiss all announcements"
            aria-label="Dismiss all announcements"
          >
            ×
          </button>
        </div>
      )}
      <div className="header">
        <div className="header-top">
          <div>
            <div className="logo"><span className="logo-f">Foody</span><span className="logo-b">Buddy</span></div>
            <div className="logo-sub">Karunya Canteen</div>
          </div>
          <div className="header-actions">
            <div onClick={onProfileClick} title="My Profile" style={{ width: 38, height: 38, borderRadius: "50%", background: "#D94F00", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ fontFamily: "inherit", fontSize: 14, fontWeight: 900, color: "white", lineHeight: 1 }}>{initials}</span>
            </div>
            {user.role === "admin" && (
              <button className="icon-btn" onClick={onAdminClick} title="Admin Panel" aria-label="Admin Panel">
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--dark)" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
              </button>
            )}
            <button className="icon-btn filled" onClick={onCartClick} style={{ position: "relative" }} aria-label="View cart">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 01-8 0" /></svg>
              {cartCount > 0 && <div className="cart-badge">{cartCount}</div>}
            </button>
          </div>
        </div>
        <div style={{ position: "relative" }}>
          <div className="search" style={{ position: "relative", display: "flex", alignItems: "center", gap: "8px" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#BDBDBD" strokeWidth="2.5"><circle cx="11" cy="11" r="6" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input
              placeholder="Search menu..."
              value={search}
              onChange={e => { setSearch(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              style={{ flex: 1 }}
            />
            {search && <button onClick={() => { setSearch(""); setShowSuggestions(false); }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--mid)", fontSize: 16, lineHeight: 1, padding: "0 2px" }}>x</button>}
            <button onClick={() => setVegFilter(vegFilter === "all" ? "veg" : vegFilter === "veg" ? "non-veg" : "all")} title="Veg / Non-veg filter" className={`filter-pill ${vegFilter !== "all" ? "active" : ""}`}>
              {vegFilter === "all" ? "All" : vegFilter === "veg" ? "Veg" : "NonVeg"}
            </button>
          </div>
          <AnimatePresence>
            {showSuggestions && suggestions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.15 }}
                style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", border: "1px solid var(--border)", borderRadius: "var(--radius)", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 100, overflow: "hidden", marginTop: 4 }}>
                {suggestions.map(item => (
                  <div key={item.id} onMouseDown={() => { setSearch(item.name); setShowSuggestions(false); setCat("all"); const h = [item.name, ...searchHistory.filter(s => s !== item.name)].slice(0, 5); setSearchHistory(h); localStorage.setItem("fb_search_history", JSON.stringify(h)); }}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid var(--border)", transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--light)"}
                    onMouseLeave={e => e.currentTarget.style.background = "white"}>
                    <div style={{ width: 36, height: 36, borderRadius: 8, overflow: "hidden", background: "var(--light)", flexShrink: 0 }}>
                      {item.image_url
                        ? <img loading="lazy" src={item.image_url} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="2"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" /><path d="M7 2v20" /><path d="M21 15V2a5 5 0 0 0-5 5v8c0 1.1.9 2 2 2h3z" /></svg>
                        </div>
                      }
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--dark)" }}>{item.name}</div>
                      <div style={{ fontSize: 11, color: "var(--mid)" }}>{item.category} · ₹{item.price}</div>
                    </div>
                    <div style={{ fontSize: 11, padding: "2px 7px", borderRadius: 4, background: item.type === "veg" ? "#ECFDF5" : "#FEF2F2", color: item.type === "veg" ? "#065F46" : "#991B1B", fontWeight: 700 }}>{item.type}</div>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {showSuggestions && !search.trim() && searchHistory.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.15 }}
                style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", border: "1px solid var(--border)", borderRadius: "var(--radius)", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 100, padding: "10px 12px", marginTop: 4 }}>
                <div style={{ fontSize: 11, color: "var(--mid)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Recent</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {searchHistory.map(s => (
                    <button key={s} onMouseDown={() => { setSearch(s); setShowSuggestions(false); }} style={{ padding: "5px 12px", borderRadius: 20, border: "1px solid var(--border)", background: "var(--light)", fontSize: 12, fontWeight: 600, color: "var(--dark)", cursor: "pointer" }}>{s}</button>
                  ))}
                  <button onMouseDown={() => { setSearchHistory([]); localStorage.removeItem("fb_search_history"); }} style={{ padding: "5px 10px", borderRadius: 20, border: "none", background: "none", fontSize: 11, color: "var(--mid)", cursor: "pointer" }}>Clear</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="cats-container">
        <div className="cats">
          {CATS.map(c => (
            <button
              key={c.key}
              className={`cat-pill ${cat === c.key ? "active" : ""}`}
              onClick={(e) => {
                setCat(c.key);
                if (navigator.vibrate) navigator.vibrate(5);
                e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
              }}
              style={{ position: "relative", background: cat === c.key ? "transparent" : "", borderColor: cat === c.key ? "transparent" : "", boxShadow: cat === c.key ? "none" : "" }}
            >
              {cat === c.key && (
                <motion.div
                  layoutId="activeCategory"
                  style={{ position: "absolute", inset: 0, background: "var(--primary)", borderRadius: 100, zIndex: -1 }}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
              <span className="cat-label" style={{ position: "relative", zIndex: 1, color: cat === c.key ? "white" : "inherit" }}>{c.label}</span>
              <span className="cat-count" style={{ position: "relative", zIndex: 1, color: cat === c.key ? "rgba(255,255,255,0.8)" : "inherit" }}>{getCategoryCount(c.key)}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="section-title">
        {cat === "all" ? "Today's Menu" : CATS.find(c => c.key === cat)?.label}
        {!loading && <span className="section-count">{filtered.length} items</span>}
      </div>

      {loading && <MenuGridSkeleton />}

      <div className="menu-grid">
        {!loading && filtered.length === 0 && (
          <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "2rem", color: "var(--mid)" }}>No items found</div>
        )}

        {filtered.map((item, idx) => {
          const qty = cart[item.id]?.qty || 0;
          return (
            <TiltCard
              initial={{ opacity: 0, y: 35 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                type: "spring",
                stiffness: 120,
                damping: 17,
                delay: Math.min(idx * 0.05, 0.4)
              }}
              className="item-card"
              key={item.id}
              onClick={() => handleCardClick(item)}
              style={{ cursor: "pointer" }}
            >
              <div className={`veg-badge ${item.type}`} title={item.type === "veg" ? "Vegetarian" : "Non-Vegetarian"} />
              <HeartIcon
                filled={favorites.has(item.id)}
                onClick={(e) => { e.stopPropagation(); toggleFavorite(item.id); }}
                className="favorite-btn-corner"
              />
              <div className="item-img">
                {item.image_url
                  ? <img loading="lazy" src={item.image_url} alt={item.name} />
                  : <div className="item-no-img">No photo</div>
                }
              </div>
              <div className="item-info">
                <div className="item-name">{item.name}</div>
                {item.rating_count > 0 && (
                  <div className="item-rating">
                    <span className="star-icon">★</span>
                    <span className="rating-val">{item.avg_rating}</span>
                    <span className="rating-count">({item.rating_count})</span>
                  </div>
                )}

                <div className="item-bottom">
                  <div>
                    <div className="item-price">₹{item.price}</div>
                  </div>
                  <div className="item-actions">
                    {qty === 0
                      ? (
                        <button
                          className={`add-btn${poppingId === item.id ? " add-btn-pop" : ""}`}
                          onClick={e => { e.stopPropagation(); handleAddClick(item); }}>+</button>
                      )
                      : (
                        <div className={`qty-ctrl${shakingId === item.id ? " qty-shake" : ""}`}>
                          <button onClick={() => removeItem(item.id)}>−</button>
                          <span>{qty}</span>
                          <button onClick={e => { e.stopPropagation(); handleAddClick(item); }}>+</button>
                        </div>
                      )
                    }
                  </div>
                </div>
              </div>
            </TiltCard>
          );
        })}
      </div>

      <div className="spacer-b" />

      <AnimatePresence>
        {cartCount > 0 && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className={`cart-bar${cartPulse ? " cart-bar-pulse" : ""}`}
          >
            <div className="cart-bar-inner" onClick={onCartClick}>
              <div>
                <div className="cart-bar-count">{cartCount} item{cartCount !== 1 ? "s" : ""}</div>
                <div className="cart-bar-total">₹{cartTotal}</div>
              </div>
              <div className="cart-bar-cta">View Cart</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {detailItem && (
          <ItemDetailSheet
            item={detailItem}
            menu={menu}
            cart={cart}
            onConfirm={handleAddonConfirm}
            onClose={() => setDetailItem(null)}
            onAddSuggestion={(s) => { addItem(s, []); }}
          />
        )}
      </AnimatePresence>

      {/* Combo suggestion popup */}
      <AnimatePresence>
        {comboSuggestion && (
          <motion.div
            initial={{ opacity: 0, y: 50, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: 50, x: "-50%" }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
            style={{ position: "fixed", bottom: "calc(100px + env(safe-area-inset-bottom))", left: "50%", zIndex: 1002, width: "calc(100% - 32px)", maxWidth: 400, touchAction: "pan-y" }}
            onTouchStart={e => { e._swipeX = e.touches[0].clientX; }}
            onTouchEnd={e => { if (Math.abs(e.changedTouches[0].clientX - e._swipeX) > 60) setComboSuggestion(null); }}
          >
            <div style={{ background: "var(--dark)", borderRadius: 14, padding: "14px 16px", boxShadow: "0 8px 32px rgba(0,0,0,0.25)", display: "flex", alignItems: "center", gap: 12 }}>
              {comboSuggestion.suggestion.image_url && (
                <img loading="lazy" src={comboSuggestion.suggestion.image_url} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginBottom: 2 }}>Goes well with your order</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "white", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{comboSuggestion.suggestion.name}</div>
                <div style={{ fontSize: 12, color: "var(--primary)", fontWeight: 600 }}>₹{comboSuggestion.suggestion.price}</div>
              </div>
              <button
                onClick={() => { addItem(comboSuggestion.suggestion, []); setComboSuggestion(null); }}
                style={{ background: "var(--primary)", color: "white", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
                Add
              </button>
              <button onClick={() => setComboSuggestion(null)} style={{ background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.5)", border: "none", borderRadius: 8, width: 30, height: 30, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>x</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
